/**
 * useCoverLetter
 *
 * Owns the cover-letter generation flow:
 * - config (length / fidelity / tone) — synced with SettingsContext so last choice persists
 * - results map { [versionId]: letter } persisted to localStorage
 * - loading flag
 * - run action that POSTs /api/ai?route=ats-optimize&action=coverLetter
 */

import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { CVData } from '../types';
import type {
  AiAnalyzeStructured,
  AiCompareStructured,
  AiAtsOptimizeStructured,
  CoverLetterLength,
  CoverLetterFidelity,
  CoverLetterTone,
} from '../lib/ai-types';
import { parseJsonResponse } from '../lib/fetch-utils';
import type { TemplateMetaItem } from './useCvState';
import { safeLocalStorageGetJson, safeLocalStorageSetJson, warnOnQuotaOnce } from '../lib/safe-storage';
import { useSettings } from '../contexts/SettingsContext';

export type { CoverLetterLength, CoverLetterFidelity, CoverLetterTone };

function loadStoredLetters(): Record<string, string> {
  const parsed = safeLocalStorageGetJson<Record<string, string>>('cv_cover_letters');
  return parsed && typeof parsed === 'object' ? parsed : {};
}

export interface UseCoverLetterParams {
  activeTemplate: string;
  templateMeta: TemplateMetaItem[];
  allTemplatesData: Record<string, CVData>;
  /** App locale — used as the letter language fallback when the version has none. */
  locale: 'fr' | 'en';
  effectiveJobDescription: string;
  aiAnalysisStructured: AiAnalyzeStructured | null;
  aiCompareStructured: AiCompareStructured | null;
  aiOptimizeStructured: AiAtsOptimizeStructured | null;
  /** CVData → plain text, supplied by the caller (depends on i18n labels). */
  getResumeText: (cv: CVData) => string;
}

export interface UseCoverLetterReturn {
  coverLetterLength: CoverLetterLength;
  setCoverLetterLength: Dispatch<SetStateAction<CoverLetterLength>>;
  coverLetterFidelity: CoverLetterFidelity;
  setCoverLetterFidelity: Dispatch<SetStateAction<CoverLetterFidelity>>;
  coverLetterTone: CoverLetterTone;
  setCoverLetterTone: Dispatch<SetStateAction<CoverLetterTone>>;

  generatedCoverLetterByVersionId: Record<string, string>;
  setGeneratedCoverLetterByVersionId: Dispatch<SetStateAction<Record<string, string>>>;

  isGeneratingCoverLetter: boolean;

  runCoverLetter: () => Promise<void>;
}

export function useCoverLetter(params: UseCoverLetterParams): UseCoverLetterReturn {
  const {
    activeTemplate,
    templateMeta,
    allTemplatesData,
    locale,
    effectiveJobDescription,
    aiAnalysisStructured,
    aiCompareStructured,
    aiOptimizeStructured,
    getResumeText,
  } = params;

  const { settings, update: updateSetting } = useSettings();

  const [coverLetterLength, setCoverLetterLengthState] = useState<CoverLetterLength>(
    settings.coverLetterLength ?? 'standard',
  );
  const [coverLetterFidelity, setCoverLetterFidelityState] = useState<CoverLetterFidelity>(
    settings.coverLetterFidelity ?? 'modere',
  );
  const [coverLetterTone, setCoverLetterToneState] = useState<CoverLetterTone>(
    (settings.coverLetterTone as CoverLetterTone) ?? 'professionnel',
  );

  const [generatedCoverLetterByVersionId, setGeneratedCoverLetterByVersionId] = useState<
    Record<string, string>
  >(loadStoredLetters);

  const [isGeneratingCoverLetter, setIsGeneratingCoverLetter] = useState(false);

  // Wrap setters to also persist to SettingsContext
  const setCoverLetterLength: Dispatch<SetStateAction<CoverLetterLength>> = useCallback(
    (value) => {
      setCoverLetterLengthState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        updateSetting('coverLetterLength', next);
        return next;
      });
    },
    [updateSetting],
  );

  const setCoverLetterFidelity: Dispatch<SetStateAction<CoverLetterFidelity>> = useCallback(
    (value) => {
      setCoverLetterFidelityState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        updateSetting('coverLetterFidelity', next);
        return next;
      });
    },
    [updateSetting],
  );

  const setCoverLetterTone: Dispatch<SetStateAction<CoverLetterTone>> = useCallback(
    (value) => {
      setCoverLetterToneState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        updateSetting('coverLetterTone', next as Settings['coverLetterTone']);
        return next;
      });
    },
    [updateSetting],
  );

  // Persist letters map.
  useEffect(() => {
    if (Object.keys(generatedCoverLetterByVersionId).length > 0) {
      void warnOnQuotaOnce(safeLocalStorageSetJson('cv_cover_letters', generatedCoverLetterByVersionId));
    }
  }, [generatedCoverLetterByVersionId]);

  // Drop letters for versions that no longer exist in templateMeta.
  useEffect(() => {
    const ids = new Set(templateMeta.map((t) => t.id));
    setGeneratedCoverLetterByVersionId((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((id) => {
        if (!ids.has(id)) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [templateMeta]);

  const runCoverLetter = useCallback(async () => {
    if (!effectiveJobDescription?.trim() && !aiAnalysisStructured) return;
    const versionId = activeTemplate;
    const cvDataForVersion = allTemplatesData[versionId];
    if (!cvDataForVersion) return;
    const versionMeta = templateMeta.find((t) => t.id === versionId);
    const targetLang = versionMeta?.lang ?? locale;
    setIsGeneratingCoverLetter(true);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route: 'ats-optimize',
          action: 'coverLetter',
          resumeText: getResumeText(cvDataForVersion),
          jobDescription: effectiveJobDescription,
          cvData: cvDataForVersion,
          targetLang,
          analysisStructured: aiAnalysisStructured,
          compareStructured: aiCompareStructured,
          optimizeStructured: aiOptimizeStructured,
          length: coverLetterLength,
          fidelityLevel: coverLetterFidelity,
          tone: coverLetterTone,
        }),
      });
      const json = await parseJsonResponse<{ error?: string; letter?: string }>(res);
      if (!res.ok) throw new Error(json.error || 'Erreur génération lettre');
      const letter = json.letter ?? '';
      setGeneratedCoverLetterByVersionId((prev) => ({ ...prev, [versionId]: letter }));
    } catch (e) {
      console.error('[Lettre de motivation] Error:', e);
      throw e;
    } finally {
      setIsGeneratingCoverLetter(false);
    }
  }, [
    activeTemplate,
    allTemplatesData,
    templateMeta,
    locale,
    effectiveJobDescription,
    aiAnalysisStructured,
    aiCompareStructured,
    aiOptimizeStructured,
    coverLetterLength,
    coverLetterFidelity,
    coverLetterTone,
    getResumeText,
  ]);

  return {
    coverLetterLength,
    setCoverLetterLength,
    coverLetterFidelity,
    setCoverLetterFidelity,
    coverLetterTone,
    setCoverLetterTone,
    generatedCoverLetterByVersionId,
    setGeneratedCoverLetterByVersionId,
    isGeneratingCoverLetter,
    runCoverLetter,
  };
}

// Private type alias for updateSetting call site above
type Settings = import('../contexts/SettingsContext').Settings;
